import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TablePagination,
  Chip,
  TextField,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
  Grid,
  Typography,
  CircularProgress,
  debounce,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  FormLabel,
  useTheme,
  Avatar,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Header from "../components/Header";
import axios from "axios";
import DownloadIcon from "@mui/icons-material/Download";
import SettingsIcon from "@mui/icons-material/Settings";
import Papa from "papaparse";
import { useUser } from "../provider/AuthProvider";
import BreadcrumbsBar from "../components/BreadcrumbsBar";
import { tokens } from "../theme";
import ConfirmDialog from "../components/ComfirmationDialog";
import stringToColor from "../services/stringToColor";
import { Done } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ServiceFeeAdminPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  const [buildings, setBuildings] = useState([]);
  const [filters, setFilters] = useState({
    buildingId: "",
    status: "",
    unitName: "",
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedIds, setSelectedIds] = useState([]);
  const [exporting, setExporting] = useState(false);

  // Data
  const [fees, setFees] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Snackbar
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "info",
  });
  const { user } = useUser();

  // Manage Dialog state
  const [manageDialog, setManageDialog] = useState({
    open: false,
    unit: null,
    records: [],
    selected: [],
    loading: false,
  });

  const [searchValue, setSearchValue] = useState("");

  // Fetch buildings for filter
  useEffect(() => {
    axios
      .get(`${API_BASE}/api/cmt/buildings/${user.id}`)
      .then((res) => setBuildings(res.data || []));
  }, [user.id]);

  // Debounced search (by unit name)
  const debouncedSetFilters = useMemo(
    () =>
      debounce((unitName) => {
        setFilters((prev) => ({ ...prev, unitName }));
        setPage(0);
      }, 400),
    []
  );

  // Fetch fees on filter or pagination change
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setSelectedIds([]);
    axios
      .get(`${API_BASE}/api/cmt/getServiceFees`, {
        params: {
          ...filters,
          page: page + 1,
          pageSize: rowsPerPage,
        },
      })
      .then((res) => {
        if (!isMounted) return;
        let feesData = res.data.data || [];
        feesData = feesData.sort((a, b) => {
          const buildingA = (a.unit?.building?.name || "").toLowerCase();
          const buildingB = (b.unit?.building?.name || "").toLowerCase();
          if (buildingA < buildingB) return -1;
          if (buildingA > buildingB) return 1;
          const unitA = (a.unit?.name || "").toLowerCase();
          const unitB = (b.unit?.name || "").toLowerCase();
          if (unitA < unitB) return -1;
          if (unitA > unitB) return 1;
          return 0;
        });
        setFees(feesData);
        setTotal(res.data.total || 0);
        setLoading(false);
      })
      .catch(() => {
        if (!isMounted) return;
        setFees([]);
        setTotal(0);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [filters, page, rowsPerPage]);

  // --- MANAGE DIALOG HANDLERS ---

  // Open manage dialog and fetch unit history
  const handleManageClick = async (unit) => {
    setManageDialog({
      open: true,
      unit,
      records: [],
      selected: [],
      loading: true,
    });
    try {
      const res = await axios.get(
        `${API_BASE}/api/cmt/service-fees/unit-history/${unit.id}`
      );
      setManageDialog((prev) => ({
        ...prev,
        records: res.data || [],
        loading: false,
        selected: [],
      }));
    } catch {
      setManageDialog((prev) => ({ ...prev, loading: false }));
      setSnack({
        open: true,
        msg: t("subscription_page.fetch_history_failed"),
        severity: "error",
      });
    }
  };

  const handleCloseManage = () => {
    setManageDialog({
      open: false,
      unit: null,
      records: [],
      selected: [],
      loading: false,
    });
  };

  const handleSelectAllDialog = (checked) => {
    setManageDialog((prev) => ({
      ...prev,
      selected: checked ? prev.records.map((r) => r.id) : [],
    }));
  };

  const handleSelectDialogRow = (id, checked) => {
    setManageDialog((prev) => ({
      ...prev,
      selected: checked
        ? [...prev.selected, id]
        : prev.selected.filter((_id) => _id !== id),
    }));
  };

  // Bulk or single Mark as Paid inside dialog
  const handleMarkPaidDialog = async (ids) => {
    if (!ids.length) return;
    setManageDialog((prev) => ({ ...prev, loading: true }));
    try {
      await axios.post(`${API_BASE}/api/cmt/service-fees/mark-paid-bulk`, {
        ids,
      });
      setSnack({ open: true, msg: t("subscription_page.marked_as_paid"), severity: "success" });
      // Refresh dialog records
      const res = await axios.get(
        `${API_BASE}/api/cmt/service-fees/unit-history/${manageDialog.unit.id}`
      );
      setManageDialog((prev) => ({
        ...prev,
        records: res.data || [],
        loading: false,
        selected: [],
      }));
    } catch {
      setSnack({
        open: true,
        msg: t("subscription_page.mark_paid_failed"),
        severity: "error",
      });
      setManageDialog((prev) => ({ ...prev, loading: false }));
    } finally {
      refreshMainTable();
    }
  };

  // Export CSV (selected or all)
  const handleExportUnitCSV = (records) => {
    if (!records || !records.length) {
      setSnack({
        open: true,
        msg: t("subscription_page.no_records_export"),
        severity: "warning",
      });
      return;
    }
    const csvData = records.map((row) => ({
      Building: row.unit?.building?.name || "-",
      Unit: row.unit?.name || "-",
      Month: row.month,
      Year: row.year,
      Status: row.status === "PAID" ? "Paid" : "Unpaid",
      "Paid Date": row.paid_date || "",
      "Approved User": row.user?.FullName || "",
      "Approved Email": row.user?.email || "",
      "Approved Phone": row.user?.phone || "",
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${records[0]?.unit?.name || "unit"}_service_fee_history.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Export selected from dialog
  const handleExportSelectedDialog = () => {
    const sel = manageDialog.records.filter((r) =>
      manageDialog.selected.includes(r.id)
    );
    handleExportUnitCSV(sel);
  };

  const refreshMainTable = () => {
    setLoading(true);
    setSelectedIds([]);
    axios
      .get(`${API_BASE}/api/cmt/getServiceFees`, {
        params: {
          ...filters,
          page: page + 1,
          pageSize: rowsPerPage,
        },
      })
      .then((res) => {
        let feesData = res.data.data || [];
        feesData = feesData.sort((a, b) => {
          const buildingA = (a.unit?.building?.name || "").toLowerCase();
          const buildingB = (b.unit?.building?.name || "").toLowerCase();
          if (buildingA < buildingB) return -1;
          if (buildingA > buildingB) return 1;
          const unitA = (a.unit?.name || "").toLowerCase();
          const unitB = (b.unit?.name || "").toLowerCase();
          if (unitA < unitB) return -1;
          if (unitA > unitB) return 1;
          return 0;
        });
        setFees(feesData || []);
        setTotal(res.data.total || 0);
        setLoading(false);
      })
      .catch(() => {
        setFees([]);
        setTotal(0);
        setLoading(false);
      });
  };

  // --- MAIN EXPORT ---
  const handleExportCSV = async () => {
    if (selectedIds.length === 0) {
      setSnack({
        open: true,
        msg: t("subscription_page.no_rows_selected"),
        severity: "warning",
      });
      return;
    }

    setExporting(true);

    try {
      // Get unique unit IDs from selected fees
      const selectedUnits = [
        ...new Set(
          fees
            .filter((fee) => selectedIds.includes(fee.id))
            .map((fee) => fee.unit?.id)
            .filter(Boolean)
        ),
      ];

      // Fetch full history for each selected unit
      const allHistoryPromises = selectedUnits.map((unitId) =>
        axios
          .get(`${API_BASE}/api/cmt/service-fees/unit-history/${unitId}`)
          .then((res) => res.data || [])
          .catch(() => [])
      );

      const allHistories = await Promise.all(allHistoryPromises);

      // Flatten all histories into a single array
      const combinedRecords = allHistories.flat();

      if (combinedRecords.length === 0) {
        setSnack({
          open: true,
          msg: t("subscription_page.no_history_data"),
          severity: "warning",
        });
        setExporting(false);
        return;
      }

      // Prepare CSV data
      const csvData = combinedRecords.map((row) => ({
        Building: row.unit?.building?.name || "-",
        Unit: row.unit?.name || "-",
        Month: row.month,
        Year: row.year,
        Status: row.status === "PAID" ? "Paid" : "Unpaid",
        "Paid Date": row.paid_date || "",
        "Approved User": row.user?.FullName || "",
        "Approved Email": row.user?.email || "",
        "Approved Phone": row.user?.phone || "",
      }));

      // Generate CSV string
      const csv = Papa.unparse(csvData);

      // Download CSV
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "selected_units_full_service_fee_history.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setSnack({
        open: true,
        msg: t("subscription_page.export_failed"),
        severity: "error",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box className="main-container mt-4">
      <BreadcrumbsBar />

      <Paper sx={{ p: 3, mt: 3, height: "75vh" }}>
        <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 12 }}>
          <Grid item size={{ xs: 12, sm: 5, md: 3 }}>
            <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 12 }}>
              <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                <FormControl
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: 1,
                    display: "flex",
                    p: 1,
                    m: 1,
                  }}
                >
                  <FormLabel component="legend">{t("subscription_page.filter")}</FormLabel>
                  <Box mt={2}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>{t("subscription_page.building")}</InputLabel>
                      <Select
                        value={filters.buildingId}
                        label={t("subscription_page.building")}
                        onChange={(e) => {
                          setFilters((f) => ({
                            ...f,
                            buildingId: e.target.value,
                          }));
                          setPage(0);
                        }}
                        className="notranslate"
                      >
                        <MenuItem value="">{t("subscription_page.all")}</MenuItem>
                        {buildings.map((b) => (
                          <MenuItem
                            key={b.id}
                            value={b.id}
                            className="notranslate"
                          >
                            {b.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box mt={2}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>{t("subscription_page.status")}</InputLabel>
                      <Select
                        value={filters.status}
                        label={t("subscription_page.status")}
                        onChange={(e) => {
                          setFilters((f) => ({ ...f, status: e.target.value }));
                          setPage(0);
                        }}
                      >
                        <MenuItem value="">{t("subscription_page.all")}</MenuItem>
                        <MenuItem value="PAID">{t("subscription_page.paid")}</MenuItem>
                        <MenuItem value="SUSPENSE">{t("subscription_page.unpaid")}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box mt={2}>
                    <TextField
                      label={t("subscription_page.unit_name")}
                      size="small"
                      value={searchValue}
                      onChange={(e) => {
                        setSearchValue(e.target.value);
                        debouncedSetFilters(e.target.value);
                      }}
                      fullWidth
                    />
                  </Box>
                </FormControl>
              </Grid>
              <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                <FormControl
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: 1,
                    display: "flex",
                    p: 1,
                    m: 1,
                  }}
                >
                  <FormLabel component="legend">{t("subscription_page.actions")}</FormLabel>

                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    disabled={selectedIds.length === 0 || exporting}
                    onClick={handleExportCSV}
                    sx={{ height: "40px", mt: 3, mb: 1, ml: 3, mr: 3 }}
                  >
                    {t("subscription_page.export", { count: selectedIds.length })}
                  </Button>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
          <Grid item size={{ xs: 12, sm: 7, md: 9 }}>
            <TableContainer
              sx={{
                height: "65vh",
                boxShadow: 3,
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedIds.length > 0 &&
                          selectedIds.length < fees.length
                        }
                        checked={
                          fees.length > 0 && selectedIds.length === fees.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds(fees.map((f) => f.id)); // select ALL
                          } else {
                            setSelectedIds([]);
                          }
                        }}
                        inputProps={{ "aria-label": "select all" }}
                      />
                    </TableCell>
                    <TableCell>{t("subscription_page.col_building")}</TableCell>
                    <TableCell>{t("subscription_page.col_unit")}</TableCell>
                    <TableCell>{t("subscription_page.col_month")}</TableCell>
                    <TableCell>{t("subscription_page.col_year")}</TableCell>
                    <TableCell>{t("subscription_page.col_status")}</TableCell>
                    <TableCell>{t("subscription_page.col_paid_date")}</TableCell>
                    <TableCell>{t("subscription_page.col_approved_by")}</TableCell>
                    <TableCell align="center">{t("subscription_page.col_actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : fees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography color="text.secondary">
                          {t("subscription_page.no_records")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    fees.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        selected={selectedIds.includes(row.id)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.includes(row.id)}
                            onChange={(e) => {
                              if (e.target.checked)
                                setSelectedIds([...selectedIds, row.id]);
                              else
                                setSelectedIds(
                                  selectedIds.filter((id) => id !== row.id)
                                );
                            }}
                          />
                        </TableCell>
                        <TableCell className="notranslate">
                          {row.unit?.building?.name || "-"}
                        </TableCell>
                        <TableCell className="notranslate">
                          {row.unit?.name || "-"}
                        </TableCell>
                        <TableCell>{row.month}</TableCell>
                        <TableCell>{row.year}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.status === "PAID" ? t("subscription_page.paid") : t("subscription_page.unpaid")}
                            color={row.status === "PAID" ? "success" : "error"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{row.paid_date || "-"}</TableCell>
                        <TableCell>
                          {row.user ? (
                            <Box
                              mt={1}
                              mb={1}
                              display="flex"
                              alignItems="center"
                              className="notranslate"
                            >
                              <Avatar
                                alt={row.user.FullName}
                                src={row.user.avatar_url}
                                sx={{
                                  mr: 1,
                                  bgcolor: stringToColor(row.user.FullName),
                                }}
                              >
                                {row.user.FullName?.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                >
                                  {row.user.FullName}
                                </Typography>
                                <Typography variant="caption">
                                  {row.user.email}
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              &mdash;
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={t("subscription_page.manage_service_fees")}>
                            <IconButton
                              color="primary"
                              onClick={() => handleManageClick(row.unit)}
                              size="small"
                            >
                              <SettingsIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* --- MANAGE DIALOG --- */}
      <Dialog
        open={manageDialog.open}
        onClose={handleCloseManage}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="notranslate">
          {manageDialog.unit?.name} ({manageDialog.unit?.building?.name})
        </DialogTitle>
        <DialogContent>
          {manageDialog.loading ? (
            <Box textAlign="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          manageDialog.selected.length ===
                            manageDialog.records.length &&
                          manageDialog.records.length > 0
                        }
                        indeterminate={
                          manageDialog.selected.length > 0 &&
                          manageDialog.selected.length <
                            manageDialog.records.length
                        }
                        onChange={(e) =>
                          handleSelectAllDialog(e.target.checked)
                        }
                      />
                    </TableCell>
                    <TableCell>{t("subscription_page.col_month")}</TableCell>
                    <TableCell>{t("subscription_page.col_year")}</TableCell>
                    <TableCell>{t("subscription_page.col_status")}</TableCell>
                    <TableCell>{t("subscription_page.col_paid_date")}</TableCell>
                    <TableCell>{t("subscription_page.col_approved_by")}</TableCell>
                    <TableCell align="center">{t("subscription_page.col_actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {manageDialog.records.map((rec) => (
                    <TableRow
                      key={rec.id}
                      hover
                      selected={manageDialog.selected.includes(rec.id)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={manageDialog.selected.includes(rec.id)}
                          onChange={(e) =>
                            handleSelectDialogRow(rec.id, e.target.checked)
                          }
                        />
                      </TableCell>
                      <TableCell>{rec.month}</TableCell>
                      <TableCell>{rec.year}</TableCell>
                      <TableCell>
                        <Chip
                          label={rec.status === "PAID" ? t("subscription_page.paid") : t("subscription_page.unpaid")}
                          color={rec.status === "PAID" ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{rec.paid_date || "-"}</TableCell>
                      <TableCell className="notranslate">
                        {rec.user?.FullName || "-"}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={t("subscription_page.mark_as_paid")}>
                          <span>
                            <IconButton
                              disabled={rec.status === "PAID"}
                              color="success"
                              onClick={() => handleMarkPaidDialog([rec.id])}
                              size="small"
                            >
                              <Done />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t("subscription_page.export_csv")}>
                          <IconButton
                            onClick={() => handleExportUnitCSV([rec])}
                            size="small"
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleMarkPaidDialog(manageDialog.selected)}
            disabled={manageDialog.selected.length === 0}
            variant="contained"
            color="success"
            startIcon={<Done />}
            sx={{
              color: "#fff",
            }}
          >
            {t("subscription_page.mark_paid_count", { count: manageDialog.selected.length })}
          </Button>
          <Button
            onClick={handleExportSelectedDialog}
            disabled={manageDialog.selected.length === 0}
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            {t("subscription_page.export_selected")}
          </Button>
          <Button
            onClick={() => handleExportUnitCSV(manageDialog.records)}
            disabled={manageDialog.records.length === 0}
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            {t("subscription_page.export_all")}
          </Button>
          <Button onClick={handleCloseManage}>{t("subscription_page.close")}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={2200}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          sx={{ width: "100%" }}
          onClose={() => setSnack({ ...snack, open: false })}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
